import React from 'react';
import { Box, Container, Grid, Typography, Link as MuiLink, Stack, Divider, IconButton } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import CallRoundedIcon from '@mui/icons-material/CallRounded';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';

export default function Footer() {
  const footerLinks = [
    { label: 'Home', path: '/' },
    { label: 'Our Services', path: '/programs' },
    { label: 'About', path: '/about' },
    { label: 'Gallery', path: '/gallery' },
    { label: 'Newsroom', path: '/newsroom' },
    { label: 'Transparency', path: '/transparency' },
    { label: 'Donate', path: '/donate' },
    { label: 'Contact', path: '/contact' }
    ,{ label: 'Privacy', path: '/privacy' }
    ,{ label: 'Terms', path: '/terms' }
    ,{ label: 'Refund Policy', path: '/refund-policy' }
  ];

  return (
    <Box sx={{ bgcolor: '#f8fafc', pt: 8, pb: 4, px: { xs: 2, md: 4 } }}>
      {/* The Floating Island Card */}
      <Container
        maxWidth="lg"
        disableGutters
        sx={{
          bgcolor: '#ffffff',
          borderRadius: '40px',
          boxShadow: '0 30px 60px -15px rgba(11, 95, 255, 0.15)',
          border: '1px solid rgba(11, 95, 255, 0.05)',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Subtle top glossy reflection */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(11, 95, 255, 0.3), transparent)',
          }}
        />

        <Box sx={{ px: { xs: 4, md: 8 }, py: { xs: 6, md: 8 } }}>
          <Grid container spacing={6} justifyContent="space-between">
            
            {/* Left: Brand & Vision */}
            <Grid item xs={12} md={5}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <VolunteerActivismIcon sx={{ color: '#0b5fff', fontSize: 42, mr: 2 }} />
                <Typography variant="h3" sx={{ color: '#0f172a', fontWeight: 900, letterSpacing: '-1px' }}>
                  ALC Trust
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ color: '#475569', fontWeight: 400, lineHeight: 1.6, mb: 4 }}>
                Creating waves of positive change. We are dedicated to transparency, support, and community growth.
              </Typography>
              
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3}>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Email</Typography>
                  <Typography component="a" href="mailto:penchal@innoture.co.in" variant="body1" sx={{ color: '#0b5fff', fontWeight: 700, mt: 0.5, display: 'block', overflowWrap: 'anywhere', textDecoration: 'none' }}>penchal@innoture.co.in</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>Phone</Typography>
                  <Typography component="a" href="tel:+919632036011" variant="body1" sx={{ color: '#0b5fff', fontWeight: 700, mt: 0.5, display: 'block', textDecoration: 'none' }}>+91-9632036011</Typography>
                </Box>
              </Stack>
            </Grid>

            {/* Right: Modern Navigation Layout */}
            <Grid item xs={12} md={6}>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', display: 'block', mb: 3 }}>
                Navigation
              </Typography>
              
              {/* Using a Masonry-style Grid for links looks much more premium than a simple list */}
              <Grid container spacing={3}>
                {footerLinks.map((item) => (
                  <Grid item xs={6} sm={4} key={item.label}>
                    <MuiLink
                      component={RouterLink}
                      to={item.path}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: '#334155',
                        fontWeight: 600,
                        fontSize: '1.05rem',
                        textDecoration: 'none',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&:hover': {
                          color: '#0b5fff',
                          transform: 'translateY(-2px)',
                        },
                        '&:hover .outward-arrow': {
                          opacity: 1,
                          transform: 'translate(4px, -4px)',
                        }
                      }}
                    >
                      {item.label}
                      <ArrowOutwardRoundedIcon 
                        className="outward-arrow"
                        sx={{ 
                          fontSize: 16, 
                          ml: 0.5, 
                          opacity: 0, 
                          position: 'absolute',
                          right: '-20px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
                        }} 
                      />
                    </MuiLink>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* The Base: Developer Signature */}
        <Box 
          sx={{ 
            bgcolor: '#f8fafc', // Slight contrast for the bottom bar inside the card
            px: { xs: 4, md: 8 }, 
            py: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            borderTop: '1px solid #e2e8f0'
          }}
        >
          <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
            © {new Date().getFullYear()} ALC Trust. All rights reserved.
          </Typography>

          {/* Bold, proud developer statement */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              '&:hover .dev-text': {
                color: '#0b5fff',
              }
            }}
          >
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              Developed & Maintained by
            </Typography>
            <Typography 
              className="dev-text"
              variant="subtitle2" 
              sx={{ 
                color: '#0f172a', // Deep slate, not black
                fontWeight: 800, 
                letterSpacing: '0.5px',
                transition: 'color 0.3s ease'
              }}
            >
              Innoture Solutions Private Limited
            </Typography>
          </Box>
        </Box>
        
      </Container>
    </Box>
  );
}
